'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface UserProfile {
  id: number;
  username: string;
  displayName: string;
  badge: string;
  badgeColor: string;
  accuracy: number;
  picks: number;
  roi: number;
  followers: number;
  rank: number;
  specialty: string;
  recentAnalysis: string;
  verified: boolean;
}

interface SharedAnalysis {
  id: number;
  author: string;
  badge: string;
  title: string;
  content: string;
  likes: number;
  comments: number;
  timestamp: string;
  tags: string[];
  accuracy: number;
}

const LEADERBOARD: UserProfile[] = [
  { id: 1, username: 'quant_puck', displayName: 'Quant Puck', badge: 'Elite Analyst', badgeColor: 'text-yellow-400', accuracy: 68.4, picks: 342, roi: 12.8, followers: 1240, rank: 1, specialty: 'xG Models', recentAnalysis: 'BOS vs TOR — Edge détecté sur total', verified: true },
  { id: 2, username: 'sharp_ice', displayName: 'Sharp Ice', badge: 'Sharp Bettor', badgeColor: 'text-blue-400', accuracy: 65.2, picks: 289, roi: 9.4, followers: 876, rank: 2, specialty: 'Line Movements', recentAnalysis: 'Mouvement sharp sur COL ML', verified: true },
  { id: 3, username: 'goalie_guru', displayName: 'Goalie Guru', badge: 'Goalie Expert', badgeColor: 'text-green-400', accuracy: 71.1, picks: 198, roi: 15.2, followers: 654, rank: 3, specialty: 'Goalie Analytics', recentAnalysis: 'Sorokin sous-évalué vs BUF', verified: false },
  { id: 4, username: 'monte_carlo_pro', displayName: 'Monte Carlo Pro', badge: 'Simulation Master', badgeColor: 'text-purple-400', accuracy: 63.8, picks: 412, roi: 7.6, followers: 543, rank: 4, specialty: 'Monte Carlo', recentAnalysis: 'Simulation EDM-CGY: OT +28%', verified: true },
  { id: 5, username: 'corsi_king', displayName: 'Corsi King', badge: 'Analytics Pro', badgeColor: 'text-cyan-400', accuracy: 62.5, picks: 267, roi: 6.2, followers: 421, rank: 5, specialty: 'Possession Metrics', recentAnalysis: 'MIN Corsi anomalie détectée', verified: false },
];

const ANALYSES: SharedAnalysis[] = [
  {
    id: 1, author: 'quant_puck', badge: 'Elite Analyst',
    title: 'BOS vs TOR — Analyse xG Complète',
    content: 'Boston présente un xGF/60 de 3.42 sur les 10 derniers matchs, soit +18% vs moyenne saison. Toronto défend en zone neutre mais concède en transition. Edge identifié sur BOS ML à -145.',
    likes: 142, comments: 28, timestamp: 'Il y a 2h', tags: ['xG', 'BOS', 'TOR', 'ML'], accuracy: 68.4,
  },
  {
    id: 2, author: 'sharp_ice', badge: 'Sharp Bettor',
    title: 'Mouvement Sharp Détecté — COL Moneyline',
    content: 'COL ML a bougé de -130 à -148 en 4h avec 72% sharp money. Mackinnon vs ligne 2 FLA = matchup favorable. Signal institutionnel fort.',
    likes: 98, comments: 15, timestamp: 'Il y a 4h', tags: ['Sharp', 'COL', 'Line Movement'], accuracy: 65.2,
  },
  {
    id: 3, author: 'goalie_guru', badge: 'Goalie Expert',
    title: 'Sorokin — Performance vs Expected Save%',
    content: 'Sorokin affiche un SV% de .928 vs xSV% de .912. Surperformance de +16 points. Historiquement, ce niveau se maintient 68% du temps sur 5 matchs suivants.',
    likes: 87, comments: 22, timestamp: 'Il y a 6h', tags: ['Goalie', 'NYI', 'xSV%'], accuracy: 71.1,
  },
];

const BADGE_TIERS = [
  { name: 'Rookie Analyst', icon: 'StarIcon', color: 'text-gray-400', req: '0-50 picks' },
  { name: 'Analytics Pro', icon: 'ChartBarIcon', color: 'text-cyan-400', req: '50-150 picks, 55%+ acc.' },
  { name: 'Sharp Bettor', icon: 'BoltIcon', color: 'text-blue-400', req: '150+ picks, 60%+ acc.' },
  { name: 'Elite Analyst', icon: 'TrophyIcon', color: 'text-yellow-400', req: '300+ picks, 65%+ acc., 8%+ ROI' },
  { name: 'Simulation Master', icon: 'BeakerIcon', color: 'text-purple-400', req: '400+ picks, spécialité sim.' },
  { name: 'Goalie Expert', icon: 'ShieldCheckIcon', color: 'text-green-400', req: '200+ picks gardiens, 68%+ acc.' },
];

type TabType = 'leaderboard' | 'analyses' | 'badges' | 'watchlist';

export default function CommunityClient() {
  const [tab, setTab] = useState<TabType>('leaderboard');
  const [likedAnalyses, setLikedAnalyses] = useState<Set<number>>(new Set());

  const toggleLike = (id: number) => {
    setLikedAnalyses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const TABS: { id: TabType; label: string; icon: string }[] = [
    { id: 'leaderboard', label: 'Classement', icon: 'TrophyIcon' },
    { id: 'analyses', label: 'Analyses', icon: 'DocumentTextIcon' },
    { id: 'badges', label: 'Badges', icon: 'StarIcon' },
    { id: 'watchlist', label: 'Watchlists', icon: 'EyeIcon' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 flex items-center justify-center">
            <Icon name="UsersIcon" size={20} className="text-yellow-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Community</h1>
            <p className="text-xs text-muted-foreground font-mono-data">Classements · Analyses · Badges · Watchlists</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border">
          <span className="w-1.5 h-1.5 rounded-full bg-positive" />
          <span className="text-xs text-muted-foreground">{LEADERBOARD.length * 48} membres actifs</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-card border border-border rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <Icon name={t.icon as Parameters<typeof Icon>[0]['name']} size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Leaderboard */}
      {tab === 'leaderboard' && (
        <div className="space-y-3">
          {LEADERBOARD.map(user => (
            <div key={user.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm font-mono-data ${user.rank === 1 ? 'bg-yellow-500/20 text-yellow-400' : user.rank === 2 ? 'bg-gray-400/20 text-gray-400' : user.rank === 3 ? 'bg-orange-500/20 text-orange-400' : 'bg-card border border-border text-muted-foreground'}`}>
                {user.rank}
              </div>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <Icon name="UserCircleIcon" size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-foreground">{user.displayName}</span>
                  {user.verified && <Icon name="CheckBadgeIcon" size={14} className="text-primary" />}
                  <span className={`text-2xs font-medium ${user.badgeColor}`}>{user.badge}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{user.specialty}</span>
                  <span>·</span>
                  <span>{user.followers} followers</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-sm font-bold font-mono-data text-positive">{user.accuracy}%</div>
                  <div className="text-2xs text-muted-foreground">Précision</div>
                </div>
                <div>
                  <div className="text-sm font-bold font-mono-data text-primary">{user.picks}</div>
                  <div className="text-2xs text-muted-foreground">Picks</div>
                </div>
                <div>
                  <div className="text-sm font-bold font-mono-data text-yellow-400">+{user.roi}%</div>
                  <div className="text-2xs text-muted-foreground">ROI</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Analyses */}
      {tab === 'analyses' && (
        <div className="space-y-4">
          {ANALYSES.map(analysis => (
            <div key={analysis.id} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 border border-primary/30 flex items-center justify-center">
                    <Icon name="UserCircleIcon" size={18} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">@{analysis.author}</span>
                      <span className="text-2xs text-primary">{analysis.badge}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{analysis.timestamp}</span>
                  </div>
                </div>
                <span className="text-xs font-mono-data text-positive">{analysis.accuracy}% acc.</span>
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-2">{analysis.title}</h3>
              <p className="text-sm text-muted-foreground mb-3 leading-relaxed">{analysis.content}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  {analysis.tags.map(tag => (
                    <span key={tag} className="text-2xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">#{tag}</span>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => toggleLike(analysis.id)} className={`flex items-center gap-1.5 text-xs transition-colors ${likedAnalyses.has(analysis.id) ? 'text-negative' : 'text-muted-foreground hover:text-negative'}`}>
                    <Icon name="HeartIcon" size={14} />
                    {analysis.likes + (likedAnalyses.has(analysis.id) ? 1 : 0)}
                  </button>
                  <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                    <Icon name="ChatBubbleLeftIcon" size={14} />
                    {analysis.comments}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Badges */}
      {tab === 'badges' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BADGE_TIERS.map((badge, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-4 flex items-start gap-3">
              <div className={`w-12 h-12 rounded-xl bg-card border border-border flex items-center justify-center flex-shrink-0 ${i < 3 ? 'opacity-100' : 'opacity-50'}`}>
                <Icon name={badge.icon as Parameters<typeof Icon>[0]['name']} size={24} className={badge.color} />
              </div>
              <div>
                <div className={`text-sm font-semibold mb-1 ${badge.color}`}>{badge.name}</div>
                <div className="text-xs text-muted-foreground">{badge.req}</div>
                {i < 3 && <span className="inline-block mt-2 text-2xs px-2 py-0.5 rounded-full bg-positive-subtle text-positive border border-positive/20">Débloqué</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Watchlist */}
      {tab === 'watchlist' && (
        <div className="space-y-3">
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="EyeIcon" size={14} className="text-primary" />
              Watchlist Publique — quant_puck
            </h3>
            <div className="space-y-2">
              {['BOS ML ce soir', 'COL Total O 5.5', 'NYR Puck Line -1.5', 'TBL vs FLA — Gardien à surveiller'].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                  <div className="flex items-center gap-2">
                    <Icon name="BookmarkIcon" size={14} className="text-primary" />
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
                  <button className="text-xs text-primary hover:text-primary/80 transition-colors">+ Suivre</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
